import {
    FunctionToolCallResponseItemDto,
    McpCallResponseItemDto,
    ResponsesMessageRecord
} from '../api/responses/responses-types';
import {
    classifyFunctionCall,
    mcpCallHasError,
    parseFunctionCallOutput,
    threadItemHasError
} from '../components/responses/threadItemVisibility';
import {
    BrowserActionCallArguments,
    describeBrowserActionCall
} from '../lib/browserActionTools';
import {getDisplayName} from '../lib/connectorDisplay';
import getGeneratedImageArtifact from '../lib/getGeneratedImageArtifact';
import getInterpreterArtifactsFromMetadata from '../lib/getInterpreterArtifactsFromMetadata';
import {getMessageRecordText} from './voiceMessageText';

/**
 * What a thread run did and produced, read from the run's records so the
 * voice assistant can narrate progress ("it is using Outlook Calendar") and
 * report the outcome ("it used Outlook Calendar and produced meetings.csv")
 * without seeing the screen. Pure functions over the thread's message array.
 */

export type RunActivityKind =
    | 'mcp'
    | 'web_search'
    | 'code'
    | 'shell'
    | 'file_search'
    | 'browser'
    | 'form'
    | 'save_to_file'
    | 'knowledge_files'
    | 'background_job'
    | 'function';

export interface RunToolActivity {
    /** Stable per activity, used to announce each one once per run. */
    key: string;
    kind: RunActivityKind;
    /** Display name of the service or tool, e.g. "Outlook Calendar". */
    label: string;
    /** Present participle for "The assistant is ...". */
    spoken: string;
    /** Past tense for "It ...". */
    done: string;
    /** Tool name for MCP calls. */
    tool?: string;
    count: number;
    failed: boolean;
}

export interface RunOutput {
    index: number;
    kind: 'image' | 'file';
    fileName: string;
    threadArtifactId?: string;
    artifactUrl?: string;
    messageId: string;
}

export interface RunOutcomeSummary {
    runId: string | null;
    replyMessageId: string | null;
    replyText: string;
    activities: RunToolActivity[];
    outputs: RunOutput[];
    /** One plain sentence, or '' when the run neither used tools nor produced files. */
    sentence: string;
}

interface ActivityDescription {
    key: string;
    kind: RunActivityKind;
    label: string;
    spoken: string;
    done: string;
    tool?: string;
}

/** "ta_list_events" becomes "list events" for speech. */
export const humanizeToolName = (name: string) =>
    name.replace(/^ta_/, '').replace(/[_-]+/g, ' ').trim();

const humanize = humanizeToolName;

const parseArguments = (
    raw: string | undefined
): BrowserActionCallArguments => {
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        return parsed && typeof parsed === 'object'
            ? (parsed as BrowserActionCallArguments)
            : {};
    } catch {
        return {};
    }
};

/**
 * Describe a tool-call record as an activity, or null for records that are not
 * tool calls (messages, reasoning) or are not worth narrating (the always-on
 * date/time tool).
 */
export const describeToolActivity = (
    record: ResponsesMessageRecord
): ActivityDescription | null => {
    switch (record.messageType) {
        case 'mcp_call': {
            const mcpCall = record.message as McpCallResponseItemDto;
            const label = getDisplayName(
                mcpCall.server_label,
                mcpCall.server_name
            );
            return {
                key: `mcp:${mcpCall.server_label}`,
                kind: 'mcp',
                label,
                spoken: `using ${label}`,
                done: `used ${label}`,
                tool: mcpCall.name
            };
        }
        case 'web_search_call':
            return {
                key: 'web_search',
                kind: 'web_search',
                label: 'Web search',
                spoken: 'searching the web',
                done: 'searched the web'
            };
        case 'code_interpreter_call':
            return {
                key: 'code',
                kind: 'code',
                label: 'Code',
                spoken: 'running code',
                done: 'ran code'
            };
        case 'shell_call':
            return {
                key: 'shell',
                kind: 'shell',
                label: 'Shell',
                spoken: 'running a shell command',
                done: 'ran a shell command'
            };
        case 'file_search_call':
            return {
                key: 'file_search',
                kind: 'file_search',
                label: 'Knowledge files',
                spoken: 'searching its knowledge files',
                done: 'searched its knowledge files'
            };
        case 'function_call': {
            const functionCall =
                record.message as FunctionToolCallResponseItemDto;
            if (functionCall.name === 'get_current_datetime') {
                return null;
            }
            const kind = classifyFunctionCall(
                functionCall,
                parseFunctionCallOutput(functionCall)
            );
            switch (kind) {
                case 'browser_action': {
                    const action = describeBrowserActionCall(
                        functionCall.name,
                        parseArguments(functionCall.arguments)
                    ).replace(/^Browser:\s*/, '');
                    return {
                        key: `browser:${functionCall.name}`,
                        kind: 'browser',
                        label: 'Browser',
                        spoken: `working in the browser (${action})`,
                        done: 'worked in the browser'
                    };
                }
                case 'web_search':
                    return {
                        key: 'web_search',
                        kind: 'web_search',
                        label: 'Web search',
                        spoken: 'searching the web',
                        done: 'searched the web'
                    };
                case 'show_form':
                    return {
                        key: 'form',
                        kind: 'form',
                        label: 'Form',
                        spoken: 'showing a form for you to fill in',
                        done: 'showed a form'
                    };
                case 'save_to_file':
                    return {
                        key: 'save_to_file',
                        kind: 'save_to_file',
                        label: 'Document template',
                        spoken: 'saving a file from its template',
                        done: 'saved a file from its template'
                    };
                case 'search_assistant_files':
                case 'fetch_assistant_files':
                    return {
                        key: 'knowledge_files',
                        kind: 'knowledge_files',
                        label: 'Knowledge files',
                        spoken: 'reading its knowledge files',
                        done: 'read its knowledge files'
                    };
                case 'background_job':
                    return {
                        key: 'background_job',
                        kind: 'background_job',
                        label: 'Background task',
                        spoken: 'starting a background task',
                        done: 'started a background task'
                    };
                case 'dynamic_session':
                    return {
                        key: 'code',
                        kind: 'code',
                        label: 'Code',
                        spoken: 'running code',
                        done: 'ran code'
                    };
                default: {
                    const label = humanize(functionCall.name);
                    return {
                        key: `function:${functionCall.name}`,
                        kind: 'function',
                        label,
                        spoken: `using ${label}`,
                        done: `used ${label}`
                    };
                }
            }
        }
        default:
            return null;
    }
};

const isRecordFailed = (record: ResponsesMessageRecord): boolean => {
    if (record.messageType === 'mcp_call') {
        return mcpCallHasError(record.message as McpCallResponseItemDto);
    }
    return threadItemHasError(record);
};

/** The run id of the most recent user message, which starts the latest run. */
export const getLatestRunId = (
    messages: ResponsesMessageRecord[]
): string | null => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const record = messages[index];
        if (record.messageType === 'user_message') {
            return record.runId || null;
        }
    }
    return null;
};

/**
 * Records belonging to the latest run: those tagged with `runId` when the tag
 * is present, else everything after the last user message.
 */
export const getLatestRunRecords = (
    messages: ResponsesMessageRecord[],
    runId: string | null = getLatestRunId(messages)
): ResponsesMessageRecord[] => {
    if (runId) {
        const byRun = messages.filter(
            record =>
                record.runId === runId && record.messageType !== 'user_message'
        );
        if (byRun.length > 0) {
            return byRun;
        }
    }
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].messageType === 'user_message') {
            return messages.slice(index + 1);
        }
    }
    return [];
};

const collectOutputs = (records: ResponsesMessageRecord[]): RunOutput[] => {
    const outputs: RunOutput[] = [];
    const seen = new Set<string>();
    const add = (output: Omit<RunOutput, 'index'>) => {
        const key =
            output.threadArtifactId ?? `${output.kind}:${output.fileName}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        outputs.push({...output, index: outputs.length + 1});
    };

    for (const record of records) {
        const image = getGeneratedImageArtifact(record.metadata);
        if (image) {
            add({
                kind: 'image',
                fileName: image.fileName,
                threadArtifactId: image.artifact?.thread_artifact_id,
                artifactUrl: image.artifact?.artifact_url,
                messageId: record.id
            });
        }
        const files = getInterpreterArtifactsFromMetadata(record.metadata);
        for (const [key, artifact] of Object.entries(files)) {
            if (artifact.is_error) {
                continue;
            }
            const fileName =
                artifact.file_name ||
                artifact.path?.split('/').pop() ||
                key.split('/').pop() ||
                key;
            add({
                kind: /\.(png|jpe?g|gif|webp|svg)$/i.test(fileName)
                    ? 'image'
                    : 'file',
                fileName,
                threadArtifactId: artifact.thread_artifact_id,
                artifactUrl: artifact.artifact_url,
                messageId: record.id
            });
        }
    }
    return outputs;
};

const countWord = (count: number) => (count === 2 ? 'twice' : `${count} times`);

const joinList = (items: string[]) =>
    items.length <= 1
        ? (items[0] ?? '')
        : items.length === 2
          ? `${items[0]} and ${items[1]}`
          : `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;

/** One sentence for the completion announcement, or '' when there is nothing to add. */
export const describeRunOutcome = (
    summary: Pick<RunOutcomeSummary, 'activities' | 'outputs'>
): string => {
    const activityPhrases = summary.activities.slice(0, 5).map(activity => {
        const detail =
            activity.kind === 'mcp' && activity.tool
                ? ` (${humanize(activity.tool)})`
                : '';
        const times = activity.count > 1 ? ` ${countWord(activity.count)}` : '';
        const failed = activity.failed ? ', which failed' : '';
        return `${activity.done}${detail}${times}${failed}`;
    });
    const omittedActivities =
        summary.activities.length - activityPhrases.length;
    if (omittedActivities > 0) {
        activityPhrases.push(`used ${omittedActivities} more tools`);
    }

    const images = summary.outputs.filter(output => output.kind === 'image');
    const files = summary.outputs.filter(output => output.kind === 'file');
    const outputPhrases: string[] = [];
    if (images.length > 0) {
        outputPhrases.push(
            images.length === 1
                ? 'generated an image'
                : `generated ${images.length} images`
        );
    }
    if (files.length > 0) {
        const names = files.slice(0, 3).map(file => file.fileName);
        const more = files.length - names.length;
        outputPhrases.push(
            `produced ${files.length} ${files.length === 1 ? 'file' : 'files'}: ${joinList(names)}${more > 0 ? ` and ${more} more` : ''}`
        );
    }

    const phrases = [...activityPhrases, ...outputPhrases];
    if (phrases.length === 0) {
        return '';
    }
    return `It ${joinList(phrases)}.`;
};

export const summarizeRunOutcome = (
    messages: ResponsesMessageRecord[],
    runId: string | null = getLatestRunId(messages)
): RunOutcomeSummary => {
    const records = getLatestRunRecords(messages, runId);

    const activities = new Map<string, RunToolActivity>();
    for (const record of records) {
        const description = describeToolActivity(record);
        if (!description) {
            continue;
        }
        const failed = isRecordFailed(record);
        const existing = activities.get(description.key);
        if (existing) {
            existing.count += 1;
            existing.failed = existing.failed || failed;
            continue;
        }
        activities.set(description.key, {...description, count: 1, failed});
    }

    const reply = [...records]
        .reverse()
        .find(record => record.messageType === 'assistant_message');

    const summary: RunOutcomeSummary = {
        runId,
        replyMessageId: reply?.id ?? null,
        replyText: reply ? getMessageRecordText(reply) : '',
        activities: Array.from(activities.values()),
        outputs: collectOutputs(records),
        sentence: ''
    };
    summary.sentence = describeRunOutcome(summary);
    return summary;
};
